<template>

  <div>

    <!-- Rate settings constants -->
    <h4 style="margin: 12px 0 8px; font-size: 16px; font-weight: 500;">Rate settings</h4>

    <!-- Info banner -->
    <el-alert
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 16px;">
      <template #default>
        These apply to every tier below. Tier-specific values (Tenor Range, Rate #1–#N, MW Rate, RRU Rate, RRU Set) live inside each tier card.
      </template>
    </el-alert>

    <el-form inline label-position="top">

      <select-sharer label="Sharer" v-model="row.organisation_id" :disabled="disabled" />
      <text-input label="Name" v-model="row.name" :disabled="disabled" />
      <check-box label="First Sharer Only" v-model="row.firstSharerOnly" :disabled="disabled" />

      <!-- XOL-319: single mutually-exclusive choice replaces the two independent
           "Rate By Sharer No / Qty" toggles (which allowed ambiguous both-on/both-off). -->
      <el-form-item label="Rate By Sharer">
        <el-radio-group v-model="sharerRateMode" :disabled="disabled">
          <el-radio-button label="none">None</el-radio-button>
          <el-radio-button label="no">By Sharer No</el-radio-button>
          <el-radio-button label="qty">By Sharer Qty</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <el-row :gutter="guts">

        <el-col :xs="24" :sm="24" :md="12" :lg="6">
          <number-input :label="toSharerLabel" :min="1" :max="10" v-model="row.toSharer" :disabled="disabled" />
          <div v-if="toSharerError" style="color:#f56c6c; font-size:12px; margin-top:4px;">{{ toSharerError }}</div>
        </el-col>

        <el-col :xs="24" :sm="24" :md="12" :lg="6">
          <number-input label="Height (metres)" :min="0" :max="121" v-model="row.height" :disabled="disabled" />
        </el-col>

      </el-row>

    </el-form>

    <hr style="border: 0; border-top: 1px solid #ebeef5; margin: 16px 0;" />

    <!-- Tier editor -->
    <sharer-rate-tiers
      ref="tiersEditor"
      v-model="row.tiers"
      :disabled="disabled"
      :to-sharer="row.toSharer"
      :validation-errors="tierValidationErrors">
    </sharer-rate-tiers>

  </div>

</template>



<script>

import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'

import SelectSharer from '@/components/atoms/selects/SelectSharer'
import SharerRateTiers from './tiers/SharerRateTiers'

export default {

  mixins: [NumberMixins, PostMixins],

  components: {
    SelectSharer,
    SharerRateTiers
  },

  props: {
    rowData: {
      type: Object,
      default: null
    }
  },

  computed: {
    disabled () {
      return !this.isManager('finance|management|admin')
    },

    // XOL-319: bridge the single radio choice to the two underlying booleans.
    // Exactly one of ratesBySharerNo / ratesBySharerQty can be true (or neither),
    // so both-on / both-off ambiguity is no longer expressible.
    sharerRateMode: {
      get () {
        if (this.row.ratesBySharerNo) return 'no'
        if (this.row.ratesBySharerQty) return 'qty'
        return 'none'
      },
      set (val) {
        this.row.ratesBySharerNo = (val === 'no')
        this.row.ratesBySharerQty = (val === 'qty')
      }
    },

    // XOL-376: label the shared "toSharer" field per the active mode. The
    // payload key stays toSharer regardless of the displayed label. Neutral
    // 'Sharers' for the unflagged mode (settled with the API agent) — None
    // falls back to the byNo-like billing path server-side, but 'Sharer
    // Count' would wrongly imply the mode is set.
    toSharerLabel () {
      if (this.row.ratesBySharerNo) return 'Sharer Count'
      if (this.row.ratesBySharerQty) return 'Sharer Qty'
      return 'Sharers'
    }
  },

  methods: {
    setTierErrors (errors) {
      this.tierValidationErrors = errors
    },

    normalizeTiers () {
      if (this.row.tiers && Array.isArray(this.row.tiers) && this.row.tiers.length > 0) {
        // Tiers present — coerce numbers, sort by seqNo, re-stamp seqNo
        var normalized = this.row.tiers
          .slice()
          .sort(function (a, b) { return (a.seqNo || 0) - (b.seqNo || 0) })
          .map(function (t, i) {
            return {
              seqNo: i + 1,
              fromMonth: Number(t.fromMonth) || 0,
              toMonth: Number(t.toMonth) || 0,
              telco1: Number(t.telco1) || 0,
              telco2: Number(t.telco2) || 0,
              telco3: Number(t.telco3) || 0,
              telco4: Number(t.telco4) || 0,
              telco5: Number(t.telco5) || 0,
              telco6: Number(t.telco6) || 0,
              telco7: Number(t.telco7) || 0,
              telco8: Number(t.telco8) || 0,
              telco9: Number(t.telco9) || 0,
              telco10: Number(t.telco10) || 0,
              mwRate: Number(t.mwRate) || 0,
              rruRate: Number(t.rruRate) || 0,
              rruSet: Number(t.rruSet) || 0
            }
          })
        this.$set(this.row, 'tiers', normalized)
      } else if (this.row.id) {
        // Existing row with flat columns — synthesize single Tier 1
        var flatTier = {
          seqNo: 1,
          fromMonth: Number(this.row.fromMonth) || 0,
          toMonth: Number(this.row.toMonth) || 0,
          telco1: Number(this.row.telco1) || 0,
          telco2: Number(this.row.telco2) || 0,
          telco3: Number(this.row.telco3) || 0,
          telco4: Number(this.row.telco4) || 0,
          telco5: Number(this.row.telco5) || 0,
          telco6: Number(this.row.telco6) || 0,
          telco7: Number(this.row.telco7) || 0,
          telco8: Number(this.row.telco8) || 0,
          telco9: Number(this.row.telco9) || 0,
          telco10: Number(this.row.telco10) || 0,
          mwRate: Number(this.row.mwRate) || 0,
          rruRate: Number(this.row.rruRate) || 0,
          rruSet: Number(this.row.rruSet) || 0
        }
        this.$set(this.row, 'tiers', [flatTier])
      } else {
        // New record — one blank Tier 1 at month 0
        var blankTier = {
          seqNo: 1,
          fromMonth: 0,
          toMonth: 12,
          telco1: 0,
          telco2: 0,
          telco3: 0,
          telco4: 0,
          telco5: 0,
          telco6: 0,
          telco7: 0,
          telco8: 0,
          telco9: 0,
          telco10: 0,
          mwRate: 0,
          rruRate: 0,
          rruSet: 0
        }
        this.$set(this.row, 'tiers', [blankTier])
      }
    },

    // Called in created() for new records only: try to pre-fill tiers from
    // the tenant's Default Tier Template. Silent on failure — the pristine
    // blank Tier 1 is already set by normalizeTiers().
    onTemplateSeed (data) {
      // Only seed if we still have the pristine single all-zero Tier 1
      // (guard against clobbering any edits or existing rows).
      if (this.row.id) return
      if (!this.row.tiers || this.row.tiers.length !== 1) return
      var t = this.row.tiers[0]
      var isPristine = (
        t.seqNo === 1 &&
        t.fromMonth === 0 &&
        t.toMonth === 12 &&
        t.telco1 === 0 && t.telco2 === 0 && t.telco3 === 0 && t.telco4 === 0 &&
        t.telco5 === 0 && t.telco6 === 0 && t.telco7 === 0 && t.telco8 === 0 &&
        t.telco9 === 0 && t.telco10 === 0 &&
        t.mwRate === 0 && t.rruRate === 0 && t.rruSet === 0
      )
      if (!isPristine) return

      if (!data || !data.tiers || !Array.isArray(data.tiers) || data.tiers.length === 0) {
        // data:null — no template saved; keep the pristine blank Tier 1
        return
      }

      // Map template tiers into the edit dialog: null → 0 (non-template edit
      // path represents blank as 0; keeps seeded dialog consistent with a
      // manually-created rate and avoids nullable rates in PR-A tests).
      var seeded = data.tiers
        .slice()
        .sort(function (a, b) { return (a.seqNo || 0) - (b.seqNo || 0) })
        .map(function (t, i) {
          return {
            seqNo: i + 1,
            fromMonth: Number(t.fromMonth) || 0,
            toMonth: Number(t.toMonth) || 0,
            telco1: Number(t.telco1) || 0,
            telco2: Number(t.telco2) || 0,
            telco3: Number(t.telco3) || 0,
            telco4: Number(t.telco4) || 0,
            telco5: Number(t.telco5) || 0,
            telco6: Number(t.telco6) || 0,
            telco7: Number(t.telco7) || 0,
            telco8: Number(t.telco8) || 0,
            telco9: Number(t.telco9) || 0,
            telco10: Number(t.telco10) || 0,
            mwRate: Number(t.mwRate) || 0,
            rruRate: Number(t.rruRate) || 0,
            rruSet: Number(t.rruSet) || 0
          }
        })
      this.$set(this.row, 'tiers', seeded)
    },

    // Called by RatesAction before save to validate tiers.
    // Returns { valid, errors } where errors is an array of per-tier error objects.
    // Delegates the boolean to the child SharerRateTiers.validate() when mounted,
    // so the authoritative isValid computed lives in one place.
    validateTiers () {
      var tiers = this.row.tiers
      var errors = []

      if (!tiers || tiers.length === 0) {
        return { valid: false, errors: errors }
      }

      // XOL-376 (R1): every tier must have Rate #1..toSharer > 0. Compute the
      // completeness limit once from row.toSharer.
      var n = Number(this.row.toSharer)
      var toSharerValid = !isNaN(n) && n >= 1 && n <= 10
      this.toSharerError = toSharerValid ? null : this.toSharerLabel + ' must be between 1 and 10.'
      var limit = toSharerValid ? Math.min(n, 10) : 0

      // Build per-tier error map (needed for field-level display regardless of
      // whether the child ref is available)
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i]
        var tierError = {}

        // Tier 1 must start at month 0
        if (i === 0 && t.fromMonth !== 0) {
          tierError.fromMonth = 'Tier 1 must start at month 0.'
        }

        // toMonth > fromMonth
        if (t.toMonth <= t.fromMonth) {
          tierError.range = 'Tier end must be greater than tier start (month ' + t.fromMonth + ').'
        }

        // Rate #1..toSharer must all be > 0 (completeness)
        var missing = []
        for (var p = 1; p <= limit; p++) {
          if (!(Number(t['telco' + p]) > 0)) missing.push(p)
        }
        if (missing.length > 0) {
          tierError.completeness = 'Rate #' + missing.join(', #') + ' required (must be greater than 0).'
        }

        errors.push(tierError)
      }

      // Delegate the authoritative boolean to the child's validate() (which uses
      // the reactive isValid computed — includes contiguity). Fall back to local
      // check when the child ref is not yet mounted (e.g. unit tests).
      var valid
      if (this.$refs && this.$refs.tiersEditor && typeof this.$refs.tiersEditor.validate === 'function') {
        valid = this.$refs.tiersEditor.validate()
      } else {
        // Local fallback: field-level errors + contiguity + toSharer range check
        var hasFieldErrors = errors.some(function (e) { return Object.keys(e).length > 0 })
        var contiguous = true
        for (var k = 1; k < tiers.length; k++) {
          if (tiers[k].fromMonth !== tiers[k - 1].toMonth) { contiguous = false; break }
        }
        valid = !hasFieldErrors && contiguous && toSharerValid
      }

      return { valid: valid, errors: errors }
    }
  },

  data () {
    return {
      guts: 12,
      row: this.rowData ? this.rowData : this.getTableStruct('rates'),
      tierValidationErrors: [],
      toSharerError: null
    }
  },

  created () {
    this.normalizeTiers()
    // For new records only: try to pre-seed tiers from the Default Tier Template.
    // Silent on GET failure — the pristine blank Tier 1 is already in place.
    // Existing rows (row.id set) are never affected.
    if (!this.row || !this.row.id) {
      this.fetchData('/rates/sharer/default-tiers', this.onTemplateSeed)
    }
  }

}
</script>
